import { createSlice } from '@reduxjs/toolkit';
import { statusTypes } from '@/constants/types';
import {
  fetchInfinitySearchProducts,
  fetchSearchResult,
  fetchTypingSearchList,
  getSearchFilterKey,
} from '../thunk/search-thunk';

const initialState = {
  currentSearchKey: '',
  searchKeys: [],
  data: [],
  isLoading: true,
  fetchStatus: statusTypes.idle,
  total: 0,
  page: 1,
  nextPageUrl: null,
  limit: 12,
  filters: {},
  error: null,
  filterKey: [],
  selectedFilterItems: null,
  selectedColors: [],
  filterParamObjects: '',
  sortingFilter: '',

  isOpenGlobalSearchModal: false,
  isSearchActive: false,

  // used to fetch data on typing
  searchedData: [],
  fetchSearchDataStatus: statusTypes.idle,
};

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    resetSearchForCache() {
      return initialState;
    },
    updateFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.page = 1;
      state.data = [];
      state.limit = 12;
      state.nextPageUrl = null;
    },
    setFilterParamObjects: (state, action) => {
      state.filterParamObjects = action.payload;
    },
    setSelectedFilterItems: (state, action) => {
      state.selectedFilterItems = action.payload;
      state.page = 1;
    },
    setSelectedColors: (state, action) => {
      state.selectedColors = action.payload;
      state.page = 1;
    },
    setSortingFilters: (state, action) => {
      state.sortingFilter = action.payload;
      state.page = 1;
    },
    resetProductFilters: (state) => {
      state.selectedColors = [];
      state.selectedFilterItems = null;
      state.filterParamObjects = '';
      state.page = 1;
    },
    setCurrentSearchKey(state, action) {
      return {
        ...state,
        currentSearchKey: action.payload,
      };
    },
    setSearchKey(state, action) {
      return {
        ...state,
        searchKeys: [...action.payload],
      };
    },
    removeSearchKey(state, action) {
      return {
        ...state,
        searchKeys: state.searchKeys.filter((key) => key !== action.payload),
      };
    },
    setIsOpenGlobalSearchModal(state, action) {
      return {
        ...state,
        isOpenGlobalSearchModal: action.payload,
      };
    },

    setIsSearchActive(state, action) {
      return {
        ...state,
        isSearchActive: action.payload,
      };
    },
    handleCloseGlobalSearchModal(state, action) {
      return {
        ...state,
        isOpenGlobalSearchModal: action.payload,
        isSearchActive: action.payload,
        currentSearchKey: '',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTypingSearchList.pending, (state) => {
        state.error = null;
        state.fetchSearchDataStatus = statusTypes.loading;
      })
      .addCase(fetchTypingSearchList.fulfilled, (state, action) => {
        const responseData = action.payload;

        if (!responseData) {
          state.searchedData = [];
        } else {
          state.searchedData = [...action.payload.items];
        }

        state.error = null;
        state.fetchSearchDataStatus = statusTypes.succeeded;
      })
      .addCase(fetchTypingSearchList.rejected, (state, action) => {
        state.fetchSearchDataStatus = statusTypes.failed;
        state.error = action.payload;
      })
      .addCase(fetchSearchResult.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.fetchStatus = statusTypes.loading;
      })
      .addCase(fetchSearchResult.fulfilled, (state, action) => {
        const responseData = action.payload;
        if (!responseData) {
          state.data = [];
          state.page = 1;
          state.limit = 12;
          state.nextPageUrl = null;
          state.total = 0;
          state.error = null;
          state.fetchStatus = statusTypes.succeeded;
        } else {
          const currentPage = responseData?.pagination?.current_page ?? 0;

          state.fetchStatus = statusTypes.succeeded;
          state.data =
            currentPage === 1
              ? [...action.payload.items]
              : [...state.data, ...action.payload.items];
          state.page = currentPage + 1;
          state.nextPageUrl = responseData?.pagination?.next ?? null;
          state.total = responseData?.pagination?.total ?? 0;
          state.limit = 12;
        }
        state.isLoading = false;
        state.error = null;
      })
      .addCase(fetchSearchResult.rejected, (state, action) => {
        state.isLoading = false;
        state.nextPageUrl = null;
        state.fetchStatus = statusTypes.failed;
        state.error = action.payload;
      })
      .addCase(fetchInfinitySearchProducts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.fetchStatus = statusTypes.fetching; // track newly fetching products
      })
      .addCase(fetchInfinitySearchProducts.fulfilled, (state, action) => {
        const responseData = action.payload;
        if (!responseData) {
          state.data = [];
          state.page = 1;
          state.limit = 12;
          state.nextPageUrl = null;
          state.total = 0;
          state.error = null;
          state.fetchStatus = statusTypes.succeeded;
        } else {
          const currentPage = responseData?.pagination?.current_page ?? 0;

          state.fetchStatus = statusTypes.succeeded;
          state.data =
            currentPage === 1
              ? [...action.payload.items]
              : [...state.data, ...action.payload.items];
          state.page = currentPage + 1;
          state.nextPageUrl = responseData?.pagination?.next ?? null;
          state.total = responseData?.pagination?.total ?? 0;
          state.limit = 12;
        }
        state.isLoading = false;
        state.error = null;
      })
      .addCase(fetchInfinitySearchProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.nextPageUrl = null;
        state.fetchStatus = statusTypes.failed;
        state.error = action.payload;
      })
      .addCase(getSearchFilterKey.pending, (state) => {
        state.error = null;
        state.filterKey = [];
        state.filterParamObjects = '';
      })
      .addCase(getSearchFilterKey.fulfilled, (state, action) => {
        state.filterKey = action.payload;
      })
      .addCase(getSearchFilterKey.rejected, (state, action) => {
        state.error = action.payload;
        state.filterKey = [];
      });
  },
});

export const {
  resetSearchForCache,
  resetCategoriesForCache,
  setSearchKey,
  setCurrentSearchKey,
  removeSearchKey,
  setIsOpenGlobalSearchModal,
  setIsSearchActive,
  handleCloseGlobalSearchModal,
  setFilterParamObjects,
  updateFilters,
  resetProductFilters,
  setSelectedFilterItems,
  setSelectedColors,
  setSortingFilters,
} = searchSlice.actions;

export default searchSlice.reducer;
